import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { CompetitionExerciseDTO, CompetitionResultEntryDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useCompetitionResults } from '../../hooks/useCompetitionResultApi';
import { useCompetitionExerciseApi } from '../../hooks/useCompetitionExerciseApi';

interface CompetitionResultEntryFormProps {
  initialValues: CompetitionResultEntryDTO;
  onSubmit: (data: CompetitionResultEntryDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const CompetitionResultEntryForm: React.FC<CompetitionResultEntryFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CompetitionResultEntryDTO>(initialValues);

  const validationRules: ValidationRules = {
    competitionResultId: [{ required: true, message: 'Competition Result is required' }],
    competitionExerciseId: [{ required: true, message: 'Competition Exercise is required' }],
    scoreId: [{ required: true, message: 'Score Id is required' }],
  };

  const { errors, validate } = useFormValidation<CompetitionResultEntryDTO>(validationRules);

  const { data: competitionResults = [], isLoading: isLoadingCompetitionResults } = useCompetitionResults();

  const [competitionExercises, setCompetitionExercises] = useState<CompetitionExerciseDTO[]>([]);
  const { search: searchCompetitionExercises, isLoading: isLoadingCompetitionExercises } = useCompetitionExerciseApi();

  useEffect(() => {
    const fetchCompetitionExercises = async () => {
      try {
        const response = await searchCompetitionExercises({
          filters: {},
          page: 1,
          pageSize: 9999,
          sortBy: '',
          sortDescending: false,
        });
        setCompetitionExercises(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching competition exercises:', error);
      }
    };

    fetchCompetitionExercises();
  }, []);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection id={FORM_SECTIONS.basicInfo.id} title={FORM_SECTIONS.basicInfo.title}>
        <FormField
          id="competitionResultId"
          label="Competition Result"
          type="dropdown"
          value={formData.competitionResultId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Competition Result"
          required
          error={errors.competitionResultId}
          options={competitionResults.map((cr) => ({
            value: cr.id,
            label: cr.competition?.name || cr.id || '',
          }))}
          isLoading={isLoadingCompetitionResults}
        />
        <FormField
          id="competitionExerciseId"
          label="Competition Exercise"
          type="dropdown"
          value={formData.competitionExerciseId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Competition Exercise"
          required
          error={errors.competitionExerciseId}
          options={competitionExercises.map((ce) => ({
            value: ce.id,
            label: `${ce.exerciseSpecificTitle || ''} (${ce.competitionClassType?.title || ''} - ${ce.gender?.title || ''})`,
          }))}
          isLoading={isLoadingCompetitionExercises}
        />
        <FormField
          id="scoreId"
          label="Score Id"
          type="text"
          value={formData.scoreId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Score Id"
          required
          error={errors.scoreId}
          helper="Paste the Score's ID (found on the Scores admin list)"
        />
        <FormField
          id="position"
          label="Position"
          type="number"
          value={formData.position ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Position"
          error={errors.position}
        />
        <FormField
          id="rank"
          label="Rank"
          type="number"
          value={formData.rank ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Rank"
          error={errors.rank}
        />
      </FormSection>
    </Form>
  );
};

export default CompetitionResultEntryForm;
